import React from 'react';
import { Target, Compass, Users, CheckCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { programs } from '../mockData';

const Programs = () => {
  const getIcon = (iconName) => {
    const icons = { Target, Compass, Users };
    return icons[iconName] || Target;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#E6B933]">Programs</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Three comprehensive pillars designed to prepare, guide, and elevate youth for long-term personal and professional success.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {programs.map((program, index) => {
              const IconComponent = getIcon(program.icon);
              return (
                <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                    <div className="inline-flex items-center space-x-3 mb-6">
                      <div className="w-16 h-16 bg-[#E6B933]/10 rounded-xl flex items-center justify-center">
                        <IconComponent className="text-[#E6B933]" size={32} />
                      </div>
                      <div>
                        <h2 className="text-4xl font-bold text-gray-900">{program.title}</h2>
                        <p className="text-[#E6B933] font-semibold text-lg">{program.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">{program.description}</p>
                    <div className="space-y-4">
                      <h3 className="font-bold text-gray-900 text-xl mb-4">Key Features:</h3>
                      {program.features.map((feature, fIndex) => (
                        <div key={fIndex} className="flex items-start space-x-3">
                          <CheckCircle className="text-[#E6B933] flex-shrink-0 mt-1" size={20} />
                          <p className="text-gray-600">{feature}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                    <Card className="p-8 bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 hover:shadow-xl transition-all duration-300">
                      <img 
                        src={index === 0 
                          ? 'https://customer-assets.emergentagent.com/job_why-me-org/artifacts/8z4wu2lr_WHYME%20BKFST.jpg'
                          : index === 1
                          ? 'https://customer-assets.emergentagent.com/job_why-me-org/artifacts/mdkmyo0b_RUNWME.jpg'
                          : 'https://customer-assets.emergentagent.com/job_why-me-org/artifacts/ru0xvpf8_ME%20TRACK%201.png'
                        }
                        alt={program.title}
                        className="w-full h-80 object-cover rounded-lg"
                      />
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Program Benefits FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Program Benefits</h2>
            <p className="text-xl text-gray-600">Frequently asked questions about our programs</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border border-gray-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#E6B933] transition-colors duration-300">
                Who can participate in WHY ME programs?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Our programs are designed for youth in Northwest Houston and Greenspoint areas, with a focus on serving At-Risk students from low-income communities. We welcome all young people seeking mentorship, academic support, and personal development opportunities.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-lg px-6 border border-gray-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#E6B933] transition-colors duration-300">
                How do I enroll my child in a program?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                You can contact us directly through our Get Involved page or call our office. We'll schedule an initial meeting to understand your child's needs and interests, then match them with the appropriate programs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border border-gray-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#E6B933] transition-colors duration-300">
                Are there any costs to participate?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                We strive to keep our programs accessible to all families. Many of our core services are provided at no cost, thanks to our generous donors and sponsors. Some specialized programs may have minimal fees, but financial assistance is always available.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border border-gray-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#E6B933] transition-colors duration-300">
                What is the NIL program for student-athletes?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Our Name, Image, and Likeness (NIL) program educates student-athletes on personal branding, marketing, and value-building. We teach them how to protect and monetize their brand while maintaining eligibility and making informed decisions about partnerships and opportunities.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-lg px-6 border border-gray-200">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#E6B933] transition-colors duration-300">
                How are programs delivered?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                Programs are delivered through a combination of in-person workshops, mentorship sessions, field trips, community events, and hands-on activities. We use our facility, partner locations, and community spaces to provide diverse learning experiences.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Programs;
